import { API_BASE_URL } from '../config/api';

export interface Appointment {
  id: string;
  dietitianId: string;
  clientId: string;
  clientName: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  type?: string;
  status?: string;
  notes?: string;
  location?: string;
  fee?: number;
  isPaid: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateAppointmentRequest {
  dietitianId: string;
  clientId: string;
  clientName: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  type?: string;
  status?: string;
  notes?: string;
  location?: string;
  fee?: number;
  isPaid?: boolean;
}

export interface CreateAppointmentResponse {
  message: string;
  appointment: Appointment;
}

export interface UpdateAppointmentRequest {
  clientId?: string;
  clientName?: string;
  appointmentDate?: string;
  startTime?: string;
  endTime?: string;
  type?: string;
  status?: string;
  notes?: string;
  location?: string;
  fee?: number;
  isPaid?: boolean;
}

export interface UpdateAppointmentResponse {
  message: string;
  appointment: Appointment;
}

export interface DeleteAppointmentResponse {
  message: string;
}

export interface ErrorResponse {
  error: string;
}

export interface TestResponse {
  message: string;
  timestamp: string;
}

export const appointmentApi = {
  async getAllAppointments(): Promise<Appointment[]> {
    const response = await fetch(`${API_BASE_URL}/appointments`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getAppointmentById(id: string): Promise<Appointment> {
    const response = await fetch(`${API_BASE_URL}/appointments/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getAppointmentsByClient(clientId: string): Promise<Appointment[]> {
    const response = await fetch(
      `${API_BASE_URL}/appointments/client/${clientId}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getAppointmentsByDietitian(dietitianId: string): Promise<Appointment[]> {
    const response = await fetch(
      `${API_BASE_URL}/appointments/dietitian/${encodeURIComponent(dietitianId)}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async getAppointmentsByDate(date: string): Promise<Appointment[]> {
    const response = await fetch(`${API_BASE_URL}/appointments/date/${date}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async createAppointment(
    appointmentData: CreateAppointmentRequest
  ): Promise<CreateAppointmentResponse> {
    const response = await fetch(`${API_BASE_URL}/appointments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appointmentData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async updateAppointment(
    id: string,
    appointmentData: UpdateAppointmentRequest
  ): Promise<UpdateAppointmentResponse> {
    const response = await fetch(`${API_BASE_URL}/appointments/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(appointmentData),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async deleteAppointment(id: string): Promise<DeleteAppointmentResponse> {
    const response = await fetch(`${API_BASE_URL}/appointments/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  async testConnection(): Promise<TestResponse> {
    const response = await fetch(`${API_BASE_URL}/appointments/test`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },
};
